import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Divider, Drawer, List, Toolbar, useMediaQuery, useTheme } from '@mui/material';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { Rol } from '@/types/enums';
import logoVertical from '@/assets/logo-vertical.png';

import { SidebarItem } from './SidebarItem';

const SIDEBAR_WIDTH_OPEN = 240;
const SIDEBAR_WIDTH_COLLAPSED = 64;

interface SidebarConfig {
  to: string;
  label: string;
  icon: JSX.Element;
  allowedRoles: readonly Rol[];
}

const SIDEBAR_ITEMS: readonly SidebarConfig[] = [
  {
    to: '/profesionales',
    label: 'Profesionales',
    icon: <PeopleIcon />,
    allowedRoles: [Rol.ADMINISTRADOR, Rol.TECNICO_LABORATORIO],
  },
  {
    to: '/establecimientos',
    label: 'Establecimientos',
    icon: <BusinessIcon />,
    allowedRoles: [Rol.ADMINISTRADOR, Rol.TECNICO_LABORATORIO],
  },
  {
    to: '/protocolos',
    label: 'Protocolos',
    icon: <DescriptionIcon />,
    allowedRoles: [Rol.ADMINISTRADOR, Rol.TECNICO_LABORATORIO, Rol.PROFESIONAL, Rol.ESTABLECIMIENTO],
  },
  {
    to: '/configuracion',
    label: 'Configuración',
    icon: <SettingsIcon />,
    allowedRoles: [Rol.ADMINISTRADOR],
  },
] as const;

interface SidebarProps {
  desktopOpen: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ desktopOpen, mobileOpen, onCloseMobile }: SidebarProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();

  const visibleItems = SIDEBAR_ITEMS.filter((item) => {
    if (!currentUser) return false;
    return item.allowedRoles.includes(currentUser.rol as Rol);
  });

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {isMobile && (
        <>
          <Toolbar />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={logoVertical}
              alt="Sistema DPV"
              sx={{ width: 'auto', maxWidth: 130, maxHeight: 90, objectFit: 'contain' }}
            />
          </Box>
          <Divider />
        </>
      )}

      <List sx={{ flexGrow: 1, py: 1 }}>
        {visibleItems.map((item) => (
          <SidebarItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            collapsed={!isMobile && !desktopOpen}
            onClick={isMobile ? onCloseMobile : undefined}
          />
        ))}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH_OPEN,
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: desktopOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: desktopOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`,
          top: 64,
          height: 'calc(100vh - 64px)',
        },
      }}
    >
      {content}
    </Drawer>
  );
}

export const SIDEBAR_WIDTHS = {
  open: SIDEBAR_WIDTH_OPEN,
  collapsed: SIDEBAR_WIDTH_COLLAPSED,
} as const;
